import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Mail, Save, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const AdminSmtpPage = () => {
  const [smtp, setSmtp] = useState({ host: '', port: 587, username: '', password: '', from_email: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await adminAPI.getSmtpSettings();
        const s = res.data.smtp || {};
        setSmtp({ host: s.host || '', port: s.port || 587, username: s.username || '', password: s.password || '', from_email: s.from_email || '' });
      } catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSmtpSettings({
        host: smtp.host,
        port: smtp.port,
        username: smtp.username,
        password: smtp.password === '••••••••' ? '' : smtp.password,
        from_email: smtp.from_email
      });
      toast.success('SMTP settings saved!');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" /></div>;

  const isConfigured = smtp.host && smtp.username;

  return (
    <div className="space-y-6 animate-fade-in-up max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>SMTP Configuration</h1>
        <p className="text-[#5F6368]">Configure email settings for password reset and notifications</p>
      </div>

      <div className="dashboard-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-[#E8F0FE] rounded-xl flex items-center justify-center">
            <Mail className="w-6 h-6 text-[#1A73E8]" />
          </div>
          <div>
            <h3 className="font-semibold text-[#202124]">Email Server Settings</h3>
            <div className="flex items-center gap-2 mt-0.5">
              {isConfigured ? (
                <span className="flex items-center gap-1 text-xs text-[#1E8E3E] bg-[#E6F4EA] px-2 py-0.5 rounded-full font-medium">
                  <CheckCircle className="w-3 h-3" /> Configured
                </span>
              ) : (
                <span className="text-xs text-[#5F6368] bg-[#F1F3F4] px-2 py-0.5 rounded-full">Not configured</span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#5F6368] mb-1">SMTP Host</label>
            <input type="text" value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })}
              className="input-google" placeholder="smtp.gmail.com" data-testid="smtp-host" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5F6368] mb-1">Port</label>
            <input type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value) || 587 })}
              className="input-google" data-testid="smtp-port" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5F6368] mb-1">Username / Email</label>
            <input type="text" value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })}
              className="input-google" placeholder="your@email.com" data-testid="smtp-username" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5F6368] mb-1">Password / App Password</label>
            <input type="password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })}
              className="input-google" placeholder="App password" data-testid="smtp-password" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-[#5F6368] mb-1">From Email Address</label>
            <input type="email" value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })}
              className="input-google" placeholder="noreply@yourdomain.com" data-testid="smtp-from" />
          </div>
        </div>

        <div className="mt-4 p-3 bg-[#F8F9FA] rounded-lg">
          <p className="text-xs text-[#5F6368]">
            <strong>Tip:</strong> For Gmail, use smtp.gmail.com (port 587) with an App Password. 
            Go to Google Account → Security → App Passwords to generate one.
          </p>
        </div>

        <button onClick={handleSave} disabled={saving} className="btn-google flex items-center gap-2 mt-4" data-testid="save-smtp-btn">
          {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          {saving ? 'Saving...' : 'Save SMTP Settings'}
        </button>
      </div>
    </div>
  );
};

export default AdminSmtpPage;
